import { inject } from "@angular/core";
import { CanActivateFn, Router, RouterStateSnapshot } from "@angular/router";
import { JwtHelperService } from "@auth0/angular-jwt";
import { InternalUserControllerService } from "../services/internal-user-controller.service";

const jwtHelper = new JwtHelperService();

function isTokenExpired(token: string): boolean {
  try {
    return jwtHelper.isTokenExpired(token);
  } catch {
    // Not a decodable JWT: we can't check expiry client-side, so let it through
    // and rely on the 401 interceptor to log out.
    return false;
  }
}

/**
 * Whether localStorage holds a usable session (username + non-expired token).
 * Shared with notFoundGuard, which has to tell a signed-in user hitting a bad URL
 * (notice + /kpi) from a signed-out one (straight to the login page).
 */
export function hasValidSession(): boolean {
  const username = localStorage.getItem("username");
  const token = localStorage.getItem("password");

  return !!(username && token && !isTokenExpired(token));
}

/**
 * Functional replacement for the old class-based AuthGuard. Allows navigation
 * when a username + non-expired token are present in localStorage; otherwise
 * clears the stale session and redirects to the login page with the returnUrl.
 */
export const authGuard: CanActivateFn = (
  _route,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const userService = inject(InternalUserControllerService);

  if (hasValidSession()) {
    return true;
  }

  userService.logout();
  return router.createUrlTree(["/"], { queryParams: { returnUrl: state.url } });
};
